import type {Call} from '@twilio/voice-sdk'
import clsx from 'clsx'

import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './Phone.module.scss'

interface KeypadProps {
  callRef: RefObject<Call | null>
  inputRef: RefObject<HTMLInputElement | null>
}

const keypadButtons: {digit: string; letters: string}[] = [
  {digit: '1', letters: ''},
  {digit: '2', letters: 'ABC'},
  {digit: '3', letters: 'DEF'},
  {digit: '4', letters: 'GHI'},
  {digit: '5', letters: 'JKL'},
  {digit: '6', letters: 'MNO'},
  {digit: '7', letters: 'PQRS'},
  {digit: '8', letters: 'TUV'},
  {digit: '9', letters: 'WXYZ'},
  {digit: '*', letters: ''},
  {digit: '0', letters: '+'},
  {digit: '#', letters: ''},
]
const VALID_DIGITS: string[] = keypadButtons.map(({digit}) => digit)

function focusElement(element: HTMLElement | null) {
  if (element) {
    element.focus()
  }
}

function scrollToEnd(node: HTMLInputElement | null) {
  if (node) {
    node.scrollLeft = node.scrollWidth
  }
}

export default function Keypad({callRef, inputRef}: KeypadProps) {
  const [pressedDigit, setPressedDigit] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const [enteredDigits, setEnteredDigits] = useState<string>('')
  const [showKeypad, setShowKeypad] = useState<boolean>(false)
  const firstButtonRef = useRef<HTMLButtonElement>(null)

  const handleDigitKeyPressOrClick = useCallback(
    (digit: string) => {
      if (callRef.current) {
        callRef.current.sendDigits(digit)
      }
      setPressedDigit(digit)
      setAnnouncement(digit)
      setEnteredDigits(prev => prev + digit)

      focusElement(inputRef.current)

      setPressedDigit(null)
    },
    [callRef, inputRef],
  )

  const handleKeyDownEvent = useCallback(
    (e: KeyboardEvent) => {
      if (VALID_DIGITS.includes(e.key)) {
        e.preventDefault()
        handleDigitKeyPressOrClick(e.key)
      }
    },
    [handleDigitKeyPressOrClick],
  )

  const handleManualInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value
      if (input.split('').every(char => VALID_DIGITS.includes(char))) {
        setEnteredDigits(input)
        if (input.length > enteredDigits.length && callRef.current) {
          const lastTypedDigit = input.charAt(input.length - 1)
          callRef.current.sendDigits(lastTypedDigit)
          setAnnouncement(lastTypedDigit)
        }
      }
    },
    [callRef, enteredDigits],
  )

  useEffect(() => {
    if (inputRef.current) {
      scrollToEnd(inputRef.current)
    }
  }, [enteredDigits, inputRef])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDownEvent)
    return () => {
      window.removeEventListener('keydown', handleKeyDownEvent)
    }
  }, [handleKeyDownEvent])

  const handleToggleKeypad = () => {
    const newShowKeypad = !showKeypad
    setShowKeypad(newShowKeypad)

    const message = newShowKeypad
      ? 'Virtual keypad is now displayed.'
      : 'Virtual keypad is now hidden.'
    setAnnouncement(message)

    if (newShowKeypad) {
      setTimeout(() => {
        firstButtonRef.current?.focus()
      }, 50)
    } else {
      focusElement(inputRef.current)
    }
  }

  return (
    <>
      <div className={style.KeypadInputContainer}>
        <input
          id='keypad-input'
          ref={inputRef}
          type='text'
          value={enteredDigits}
          onChange={handleManualInputChange}
          className={style.KeypadInput}
          placeholder='Enter digits'
          aria-label='Keypad input field'
        />
        <button
          onClick={handleToggleKeypad}
          className={style.ShowKeypadButton}
          aria-label={showKeypad ? 'Hide virtual keypad' : 'Show virtual keypad'}
          aria-expanded={showKeypad}
        >
          {showKeypad ? 'Hide keypad' : 'Show keypad'}
        </button>
      </div>

      <VisuallyHidden aria-live='polite'>{announcement}</VisuallyHidden>

      {showKeypad && (
        <div className={style.Keypad} role='group' aria-label='Phone keypad'>
          {keypadButtons.map(({digit, letters}) => (
            <button
              key={digit}
              ref={digit === '1' ? firstButtonRef : undefined}
              className={clsx(style.KeypadButton, {
                [style.KeypadButtonPressed]: pressedDigit === digit,
              })}
              onClick={() => {
                handleDigitKeyPressOrClick(digit)
              }}
              tabIndex={0}
              aria-label={letters ? `${digit} (${letters.split('').join(', ')})` : digit}
            >
              <span className={style.KeypadDigit}>{digit}</span>
              {letters && <span className={style.KeypadLetters}>{letters}</span>}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
